import React, { useEffect, useRef } from "react";
import { Box, Button, Typography, Paper, useTheme } from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { useAuth } from "../AuthContext";
import { useLoginModal } from "./LoginModalController";
import { tokens } from "../theme";

export default function ProtectedRoute({
  children,
  allowedRoles = null,
  fallbackMessage = "No tienes permisos para ver esta sección.",
  hideContentOnDeny = true,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { isAuthenticated, isInitialized, user } = useAuth();
  const { openLogin, loginOpen } = useLoginModal();
  const prompted = useRef(false);

  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated && !prompted.current && !loginOpen) {
      prompted.current = true;
      openLogin();
    }
  }, [isInitialized, isAuthenticated, openLogin, loginOpen]);

  if (!isInitialized) {
    return (
      <Box p={3}>
        <Typography>Cargando sesión…</Typography>
      </Box>
    );
  }

  const isAdmin = !!user?.is_admin;
  const isStaff = !!user?.is_staff;

  const hasRoleAccess =
    !allowedRoles ||
    allowedRoles.length === 0 ||
    (allowedRoles.includes("admin") && isAdmin) ||
    (allowedRoles.includes("staff") && isStaff);

  const deniedCard = (
    <Box
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: { xs: 8, sm: 10 },
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: "min(640px, 100%)",
          borderRadius: 3,
          p: { xs: 2.2, sm: 3 },
        }}
      >
        <Box display="flex" gap={1.5} alignItems="center" mb={1}>
          <LockRoundedIcon />
          <Typography variant="h5" fontWeight={900}>
            Acceso restringido
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: !isAuthenticated ? 2 : 0 }}>
          {!isAuthenticated
            ? "Por favor inicia sesión para ver esta sección."
            : fallbackMessage}
        </Typography>

        {!isAuthenticated && (
          <Button
            fullWidth
            variant="contained"
            onClick={openLogin}
            sx={{
              backgroundColor: colors.green[200],
              color: colors.primary[200],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              maxWidth: 260,
              mx: "auto",
              display: "block",
              "&:hover": {
                backgroundColor: colors.green[100],
              },
            }}
          >
            Iniciar sesión
          </Button>
        )}
      </Paper>
    </Box>
  );

  if (!isAuthenticated || !hasRoleAccess) {
    if (hideContentOnDeny) {
      return deniedCard;
    }

    return (
      <Box position="relative">
        <Box sx={{ pointerEvents: "none", opacity: 0.35, filter: "blur(0.2px)" }}>
          {children}
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: { xs: 10, sm: 12 },
            px: 2,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: "min(640px, 100%)",
              borderRadius: 3,
              p: { xs: 2.2, sm: 3 },
            }}
          >
            <Box display="flex" gap={1.5} alignItems="center" mb={1}>
              <LockRoundedIcon />
              <Typography variant="h5" fontWeight={900}>
                Acceso restringido
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: !isAuthenticated ? 2 : 0 }}>
              {!isAuthenticated
                ? "Por favor inicia sesión para ver esta sección."
                : fallbackMessage}
            </Typography>

            {!isAuthenticated && (
              <Button
                fullWidth
                variant="contained"
                onClick={openLogin}
                sx={{
                  backgroundColor: colors.green[200],
                  color: colors.primary[200],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                  maxWidth: 260,
                  mx: "auto",
                  display: "block",
                }}
              >
                Iniciar sesión
              </Button>
            )}
          </Paper>
        </Box>
      </Box>
    );
  }

  return children;
}